import Chip from "@mui/material/Chip";
import { alpha, type Theme } from "@mui/material/styles";
import { styled } from "../../../../lib/styled";

interface PillProps {
  label: string;
  /**
   * Visual weight: a quiet marker (`muted`), a brand-tinted inviting one
   * (`accent`), or a stand-out amber label (`warning`, e.g. an alpha notice).
   */
  tone?: "muted" | "accent" | "warning";
  /** When provided, the pill becomes an actionable button. */
  onClick?: () => void;
}

function toneStyles(theme: Theme, tone: NonNullable<PillProps["tone"]>) {
  if (tone === "accent") {
    return {
      color: theme.palette.primary.dark,
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.18) },
    };
  }
  if (tone === "warning") {
    const amber = theme.verdicts.PARTIALLY_SUPPORTED;
    return {
      color: amber.contrastText,
      backgroundColor: amber.light,
      border: `1px solid ${alpha(amber.main, 0.5)}`,
      letterSpacing: "0.02em",
    };
  }
  return {
    color: theme.palette.text.secondary,
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
    "&:hover": { backgroundColor: alpha(theme.palette.text.primary, 0.07) },
  };
}

const PillChip = styled(Chip)<{ $tone: NonNullable<PillProps["tone"]> }>(({ theme, $tone }) => ({
  borderRadius: 999,
  ...toneStyles(theme, $tone),
}));

/**
 * A small rounded label. Quiet metadata marker by default; pass `onClick` to make
 * it an actionable chip (e.g. an example to load). Closed props, no `sx` passthrough.
 */
export function Pill({ label, tone = "muted", onClick }: PillProps) {
  return <PillChip label={label} onClick={onClick} clickable={Boolean(onClick)} $tone={tone} />;
}
